/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  IApplicationMenuScreenModel,
  IAppState,
  MenuBackgroundStyle,
  MenuStyle,
  ScreenType,
  useScreenConfiguration,
  useTheme,
} from "@bms/common";
import cx from "classnames";
import Color from "color";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";

import {
  AppHeaderVisibility,
  ApplicationHeader,
  ApplicationMenu,
} from "components";
import { Button } from "components/DesignSystem/Button";
import BackIcon from "resources/icons/navigation/Arrow_left.svg";

import { HeaderLogo, HeaderSignIn, HeaderUserInfo } from "..";

import "./DesktopHeader.scss";

interface IDesktopHeader {
  visibility?: AppHeaderVisibility;
  showBackButton?: boolean;
}

export const DesktopHeader = ({
  visibility = AppHeaderVisibility.Default,
  showBackButton = false,
}: IDesktopHeader) => {
  const auth = useSelector((state: IAppState) => state.auth);
  const { t } = useTranslation();
  const history = useHistory();
  const { themeProvider } = useTheme();
  const screen = useScreenConfiguration<IApplicationMenuScreenModel>(
    ScreenType.ApplicationMenu,
  );

  const headerBackgroundColor = Color(
    themeProvider.getColor("HeaderBackgroundColor"),
  );

  const { headerStyle, headerClassName } = useMemo(() => {
    const style: React.CSSProperties = {};
    let className = "DesktopHeader";
    const menyStyle: MenuStyle = screen?.Style || MenuStyle.Default;
    const menuBackgroundStyle: MenuBackgroundStyle =
      screen?.BackgroundStyle || MenuBackgroundStyle.Default;

    if (menyStyle == MenuStyle.Default) {
      style.paddingTop = "2rem";
      style.paddingBottom = "2rem";
    } else if (menyStyle == MenuStyle.Compact) {
      className += " DesktopHeader--compact";
    }

    if (
      menuBackgroundStyle == MenuBackgroundStyle.Default ||
      menuBackgroundStyle == MenuBackgroundStyle.Gradient
    ) {
      style.background = `linear-gradient(180deg, ${headerBackgroundColor.string()}, transparent)`;
    } else {
      style.background = `${headerBackgroundColor.alpha(0.5).string()}`;
    }

    return { headerStyle: style, headerClassName: className };
  }, [headerBackgroundColor, screen?.BackgroundStyle, screen?.Style]);

  return (
    <header className={cx(headerClassName)} style={headerStyle}>
      {showBackButton && (
        <Button
          variant="tertiary"
          wrapperClassName="DesktopHeader__BackButton"
          onClick={history.goBack}
          aria-label={t("COMMON__BUTTON_GO_BACK")}
          tooltip={t("COMMON__BUTTON_GO_BACK")}
          tooltipAlign="left"
          tooltipOffset={60}
          iconOnly
        >
          <BackIcon />
        </Button>
      )}
      <div className="DesktopHeader__Left">
        <HeaderLogo />
        {!!(visibility & AppHeaderVisibility.Menu) && <ApplicationMenu />}
      </div>
      <div className="DesktopHeader__Right">
        {!!(visibility & AppHeaderVisibility.Header) && <ApplicationHeader />}
        {!!(visibility & AppHeaderVisibility.UserInfo) && (
          <HeaderUserInfo user={auth.user} />
        )}
        {!!(visibility & AppHeaderVisibility.SignIn) && <HeaderSignIn />}
      </div>
    </header>
  );
};
